import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Notifications = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMessage, setNotifMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/notifications`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]);

  const openAddModal = () => {
    setEditingId(null);
    setNotifTitle('');
    setNotifMessage('');
    setShowModal(true);
  };

  const openEditModal = (notif) => {
    setEditingId(notif.id);
    setNotifTitle(notif.title);
    setNotifMessage(notif.message);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setNotifTitle('');
    setNotifMessage('');
  };

  const handleSave = async (status) => {
    if (!notifTitle || !notifMessage) {
        alert("Title and message are required.");
        return;
    }
    
    try {
      setIsSaving(true);
      const idToken = await user.getIdToken();
      const payload = { title: notifTitle, message: notifMessage, status };
      
      let url = `${apiUrl}/api/v1/admin/notifications`;
      let method = 'POST';
      
      if (editingId) {
          url = `${apiUrl}/api/v1/admin/notifications/${editingId}`;
          method = 'PUT';
      }

      const res = await fetch(url, {
        method,
        headers: { 
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        alert(`Notification ${status === 'published' ? 'published' : 'saved as draft'} successfully!`);
        closeModal();
        fetchNotifications();
      } else {
        alert("Failed to save notification.");
      }
    } catch (error) {
      console.error("Error saving notification:", error);
      alert("Error saving notification");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this notification?")) return;
    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/notifications/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        fetchNotifications();
      } else {
        alert("Failed to delete notification.");
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      alert("Error deleting notification.");
    }
  };

  const handlePublish = async (notif) => {
    if (!window.confirm("Are you sure you want to publish this draft?")) return;
    try {
      const idToken = await user.getIdToken();
      const payload = { title: notif.title, message: notif.message, status: 'published' };
      const res = await fetch(`${apiUrl}/api/v1/admin/notifications/${notif.id}`, {
        method: 'PUT',
        headers: { 
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        fetchNotifications();
      } else {
        alert("Failed to publish notification.");
      }
    } catch (error) {
      console.error("Error publishing notification:", error);
      alert("Error publishing notification.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-6xl mx-auto py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
              Notification Management
              {loading && <span className="ml-4 text-sm font-normal text-gray-500 animate-pulse">Loading...</span>}
            </h2>
            <button 
                onClick={openAddModal}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors"
            >
                Add Notification
            </button>
          </div>

          {!user ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                <p>Please log in to view this page.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {notifications.length > 0 ? (
                        notifications.map((notif) => {
                          const status = notif.status || (notif.type === 'broadcast' ? 'published' : 'draft');
                          return (
                          <tr key={notif.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {new Date(notif.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800 dark:text-gray-200">
                              {notif.title}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                              {notif.message}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status === 'published' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {status === 'draft' && (
                                  <button onClick={() => handlePublish(notif)} className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 mr-3">Publish</button>
                              )}
                              <button onClick={() => openEditModal(notif)} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3">Edit</button>
                              <button onClick={() => handleDelete(notif.id)} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300">Delete</button>
                            </td>
                          </tr>
                        )})
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                            <p>No notifications found.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
            </div>
          )}
        </main>
      </div>

      {/* Modal Overlay */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full border border-gray-200 dark:border-gray-700">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                  {editingId ? 'Edit Notification' : 'Add Notification'}
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                        <input 
                            type="text" 
                            required
                            value={notifTitle}
                            onChange={(e) => setNotifTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-sm"
                            placeholder="e.g. System Maintenance"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                        <textarea 
                            required
                            rows="4"
                            value={notifMessage}
                            onChange={(e) => setNotifMessage(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-sm"
                            placeholder="Type your message here..."
                        ></textarea>
                    </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 flex flex-col sm:flex-row sm:justify-end gap-2">
                <button 
                  type="button" 
                  onClick={closeModal}
                  disabled={isSaving}
                  className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none disabled:opacity-50"
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  onClick={() => handleSave('draft')}
                  disabled={isSaving}
                  className="w-full sm:w-auto px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md shadow-sm text-sm font-medium focus:outline-none disabled:opacity-50"
                >
                  Save as Draft
                </button>
                <button 
                  type="button" 
                  onClick={() => handleSave('published')}
                  disabled={isSaving}
                  className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm text-sm font-medium focus:outline-none disabled:opacity-50"
                >
                  Publish
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
